import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  countryCodes,
  getCountryByCode,
  getCountryByCodeOrDial,
  findCountryByPhone,
  defaultCountry,
  reorderForDisplay,
} from '../../utils/countryCodes';
import { detectCountryFromIP } from '../../utils/ipDetection';
import { normalizeDigits } from '../../utils/normalizeDigits';
import { FiChevronDown } from 'react-icons/fi';
import styles from './PhoneInput.module.css';

const PhoneInput = ({
  value,
  onChange,
  placeholder = '050-000-0000',
  disabled,
  error,
  defaultCountryCode,
  salonCountryCode,
  onSubmit,
  testId,
  hideFlag = false,
  separated = false,
  // Start at `defaultCountryCode` and use the shopper's IP only when there is
  // none. A shop's checkout passes this: its own country is a better guess
  // than where the shopper happens to be (UX-SHOP-13). Off by default, so the
  // screens that want the visitor's country keep it.
  preferDefaultCountry = false,
  /* THE PHONE'S OWN AUTOFILL DOES THE TYPING (2026-09-29). The box holds the NATIONAL number, and says so
     (`tel-national`), so an autofilled "+972 52-612-3456" lands as a country and a number rather than as digits the
     field refuses; the keyboard is the phone pad, and nothing "corrects" a number. The Enter key's word is the
     caller's: "next" on a checkout that goes on to the address, "done" where the number is the last thing asked. */
  enterKeyHint = 'done',
}) => {
  const { t } = useTranslation();
  const countryList = countryCodes;
  const getCountryName = (c) => t(`countries.${c.code}`, { defaultValue: c.name });
  const effectiveDefault = countryList[0] || defaultCountry;
  const initialCountry = defaultCountryCode
    ? getCountryByCode(defaultCountryCode) || effectiveDefault
    : effectiveDefault;

  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const syncingFromParent = useRef(false);
  // Mirror of phoneNumber so the inbound useEffect can compare
  // against current state without depending on it (would otherwise
  // cause a reset loop on user input).
  const phoneNumberRef = useRef('');
  // The person has given this field something: typed, pasted, autofilled, or
  // picked a country. From then on the country is theirs (see the default below).
  const touchedRef = useRef(false);
  // The parent's current value, for an answer that lands after this render.
  const valueRef = useRef(value);
  valueRef.current = value;

  // DEFAULT COUNTRY — for an EMPTY field nobody has touched, and for nothing else.
  // Prefer IP detection (user location), fallback to salon or explicit prop; with
  // `preferDefaultCountry`, the explicit/salon country wins and the IP is the
  // fallback.
  //
  // The IP answer lands whenever the network lets it. Only the typed digits used
  // to be checked, and only on the shop-country path: the IP answer itself was
  // applied whenever it arrived. So a number typed with its +972 — or opened with
  // one, as "Change country code" at the checkout does — was re-badged +353 when
  // the lookup said Ireland, and a country just picked was taken back. A late
  // answer now asks again, when it lands, whether the field is still empty and
  // untouched; an answer that arrives for an older render is ignored.
  useEffect(() => {
    let stale = false;
    const fallbackCountry = defaultCountryCode || salonCountryCode;
    const emptyAndUntouched = () =>
      !touchedRef.current && !String(valueRef.current || '').trim() && !phoneNumberRef.current;
    if (!emptyAndUntouched()) return undefined;
    const mayApply = () => !stale && emptyAndUntouched();
    const applyFallback = () => {
      if (!mayApply() || !fallbackCountry) return false;
      const country = getCountryByCodeOrDial(fallbackCountry);
      if (country) setSelectedCountry(country);
      return !!country;
    };
    if (preferDefaultCountry && fallbackCountry && getCountryByCodeOrDial(fallbackCountry)) {
      applyFallback();
      return () => { stale = true; };
    }
    detectCountryFromIP()
      .then((code) => {
        if (!mayApply()) return;
        const country = code ? getCountryByCode(code) : null;
        if (country) setSelectedCountry(country);
        else applyFallback();
      })
      .catch(() => applyFallback());
    return () => { stale = true; };
  }, [defaultCountryCode, salonCountryCode, preferDefaultCountry]);

  // Keep ref in sync with state so the inbound useEffect can read
  // the latest phoneNumber without becoming dependent on it.
  useEffect(() => { phoneNumberRef.current = phoneNumber; }, [phoneNumber]);

  // Sync from parent value (including pasted full numbers)
  useEffect(() => {
    // Short-circuit when the incoming parent value is just the ASCII
    // round-trip of what we already display. Stops the user's Arabic
    // / Persian glyph from being overwritten with ASCII on the
    // re-render following our own outbound sync.
    if (value) {
      const internalAscii = normalizeDigits(phoneNumberRef.current || '').replace(/\D/g, '');
      const ccDigits = (selectedCountry?.dialCode || '').replace(/\D/g, '');
      const internalNational = ccDigits && internalAscii.startsWith(ccDigits)
        ? internalAscii.slice(ccDigits.length).replace(/^0+/, '') || internalAscii.slice(ccDigits.length)
        : internalAscii.replace(/^0+/, '') || internalAscii;
      const internalFull = internalNational ? `${selectedCountry.dialCode}${internalNational}` : '';
      if (phoneNumberRef.current && value.trim() === internalFull) {
        return;
      }
    }
    /* THE FLAG RISES ONLY WHEN THE PARENT CHANGES THE FIELD (2026-10-02). It used to rise on every run of this
     * effect — the mount with an empty value included — and fall a frame later; the outbound sync below is skipped
     * while it is up, so a number typed inside that first frame never reached the page (the checkout's contact
     * alert then stayed over a filled form). A sync that changes nothing raises nothing. */
    let country = null;
    let next = '';
    if (value) {
      country = findCountryByPhone(value);
      if (country) {
        const codeDigits = country.dialCode.replace(/\D/g, '');
        const digits = String(value).replace(/\D/g, '');
        next = digits.startsWith(codeDigits)
          ? digits.slice(codeDigits.length).replace(/^0+/, '') || digits.slice(codeDigits.length)
          : digits.replace(/^0+/, '') || digits;
      } else {
        next = value.replace(/\D/g, '');
      }
    }
    const countryChanges = !!country && country.code !== selectedCountry?.code;
    if (next === (phoneNumberRef.current || '') && !countryChanges) return;
    syncingFromParent.current = true;
    if (country) setSelectedCountry(country);
    setPhoneNumber(next);
    requestAnimationFrame(() => { syncingFromParent.current = false; });
  }, [value]);

  // Sync internal → parent. normalizeDigits first so an Arabic /
  // Persian internal value resolves to ASCII digits — parent state
  // is always 0–9 even though the displayed field can be ٠–٩ / ۰–۹.
  useEffect(() => {
    /* The flag is consumed HERE, on the render the inbound change caused — never only on a later animation frame,
     * which the test clock, a background tab or a busy phone can hold back past the next keystroke. */
    if (syncingFromParent.current) { syncingFromParent.current = false; return; }
    const digits = normalizeDigits(phoneNumber || '').replace(/\D/g, '');
    const codeDigits = selectedCountry.dialCode.replace(/\D/g, '');
    let national = digits;
    if (codeDigits && digits.startsWith(codeDigits)) {
      national = digits.slice(codeDigits.length).replace(/^0+/, '') || digits.slice(codeDigits.length);
    } else if (digits.startsWith('0')) {
      national = digits.replace(/^0+/, '') || digits;
    }
    const full = national ? `${selectedCountry.dialCode}${national}` : '';
    const current = (value && typeof value === 'string') ? value.trim() : '';
    if (full !== current) onChange(full);
  }, [phoneNumber, selectedCountry, value, onChange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Use the whole component as the boundary so a click on the toggle
      // button isn't treated as "outside" — that caused mousedown→close then
      // click→reopen, leaving the dropdown stuck open.
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePhoneChange = (e) => {
    // Accept Arabic-Indic (٠–٩) and Persian (۰–۹) digits as digits so
    // the user keeps SEEING their typed glyphs in the field. The
    // conversion to ASCII happens on the way OUT to the parent (so
    // parent state / submit / validation always see 0–9).
    touchedRef.current = true;
    const raw = e.target.value;
    const digits = raw.replace(/[^0-9٠-٩۰-۹]/g, '');
    const ascii = normalizeDigits(digits).replace(/\D/g, '');
    const looksInternational = raw.trimStart().startsWith('+') || raw.trimStart().startsWith('00');
    // Browser/iOS autofill (and paste) drop a whole number at once and often
    // include the country code WITHOUT a leading '+'. Detect that bulk write
    // (length jumps by more than one keystroke) and strip the country code so
    // it lands in the selector, not the national field. Prefer the already-
    // selected country, then fall back to detecting it from the digits.
    const prevLen = normalizeDigits(phoneNumber || '').replace(/\D/g, '').length;
    const bulk = ascii.length - prevLen > 1;
    if (looksInternational || (bulk && ascii.length > 11)) {
      const selCc = (selectedCountry?.dialCode || '').replace(/\D/g, '');
      if (selCc && ascii.startsWith(selCc) && ascii.length > selCc.length) {
        setPhoneNumber(ascii.slice(selCc.length));
        return;
      }
      const country = findCountryByPhone(ascii);
      if (country) {
        const codeDigits = country.dialCode.replace(/\D/g, '');
        const national = ascii.startsWith(codeDigits) ? ascii.slice(codeDigits.length) : ascii;
        setSelectedCountry(country);
        setPhoneNumber(national);
        return;
      }
    }
    setPhoneNumber(digits);
  };

  const handleBlur = () => {
    // Catch browser / iOS Safari autofill that wrote the DOM input WITHOUT
    // firing React's onChange — adopt the field's real value (routed through
    // the normal change handler so the country code is split out). Otherwise
    // the parent's phone stays empty and "phone is required" fires even though
    // the field visibly shows a number.
    const domRaw = inputRef.current?.value || '';
    const domDigits = normalizeDigits(domRaw).replace(/\D/g, '');
    const stateDigits = normalizeDigits(phoneNumber || '').replace(/\D/g, '');
    if (domDigits && domDigits !== stateDigits) {
      handlePhoneChange({ target: { value: domRaw } });
      return;
    }
    const digits = (phoneNumber || '').replace(/\D/g, '');
    const codeDigits = selectedCountry.dialCode.replace(/\D/g, '');
    if (codeDigits && digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
      const national = digits.slice(codeDigits.length);
      setPhoneNumber(national);
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').trim();
    if (!pasted) return;
    touchedRef.current = true;
    const looksInternational = pasted.startsWith('+') || pasted.startsWith('00');
    if (!looksInternational) return;
    const digits = pasted.replace(/\D/g, '');
    if (digits.length <= 11) return;
    const country = findCountryByPhone(digits);
    if (country) {
      e.preventDefault();
      const codeDigits = country.dialCode.replace(/\D/g, '');
      const national = digits.startsWith(codeDigits) ? digits.slice(codeDigits.length) : digits;
      setSelectedCountry(country);
      setPhoneNumber(national);
    }
  };

  const handleCountrySelect = (country) => {
    touchedRef.current = true;
    setSelectedCountry(country);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const filtered = countryList.filter(
    (c) =>
      getCountryName(c).toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.dialCode.includes(searchQuery) ||
      c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={`${styles.wrapper} ${error ? styles.error : ''} ${separated ? styles.separated : ''}`} style={{ direction: 'ltr' }}>
        <button
          type="button"
          className={styles.countrySelector}
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled}
          // The country half of the field has its own handle, so a browser test
          // can choose a country the way a person does (e2e/helpers/phone-input.ts).
          data-testid={testId ? `${testId}-country` : undefined}
          data-country={selectedCountry.code}
        >
          {!hideFlag && <span className={styles.flag}>{selectedCountry.flag}</span>}
          <span className={styles.dialCode}>{selectedCountry.dialCode}</span>
          <FiChevronDown className={`${styles.chevron} ${showDropdown ? styles.rotated : ''}`} />
        </button>
        <input
          ref={inputRef}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          name="tel-national"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint={enterKeyHint}
          value={phoneNumber}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.keyCode === 13) && onSubmit) {
              e.preventDefault();
              onSubmit();
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={styles.input}
          aria-label="Phone number"
          data-testid={testId}
          required
        />
      </div>
      {error && <div className={styles.errorMessage}>{error}</div>}

      {showDropdown && (
        <div className={styles.dropdown} ref={dropdownRef}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder={t('searchCountry', { defaultValue: 'Search country...' })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              data-testid={testId ? `${testId}-country-search` : undefined}
            />
          </div>
          <div className={styles.countryList}>
            {reorderForDisplay(filtered).map((c) => (
              <button
                key={c.code}
                type="button"
                className={`${styles.option} ${selectedCountry.code === c.code ? styles.selected : ''}`}
                onClick={() => handleCountrySelect(c)}
                data-testid={testId ? `${testId}-country-${c.code}` : undefined}
              >
                <span className={styles.flag}>{c.flag}</span>
                <span className={styles.name}>{getCountryName(c)}</span>
                <span className={styles.dial}>{c.dialCode}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
